(() => {
  class PupsCollectionAdPin {
    constructor(root) {
      this.root = root;
      this.params = new URLSearchParams(window.location.search);
      this.collectionUrl = root.dataset.pupsCollectionUrl;
      const explicitHandle = this.normalizeHandle(this.params.get('first'));
      const explicitVariant = this.normalizeVariant(this.params.get('variant'));
      const storedEntry = explicitHandle ? null : window.PupsEntryProduct?.get?.();
      const collectionHandle = this.collectionHandle(this.collectionUrl);
      const matchesStoredCollection =
        storedEntry &&
        this.normalizeHandle(storedEntry.handle) &&
        this.normalizeHandle(storedEntry.collectionHandle) === collectionHandle;
      this.handle = explicitHandle || (matchesStoredCollection ? this.normalizeHandle(storedEntry.handle) : '');
      this.variant =
        explicitVariant || (matchesStoredCollection ? this.normalizeVariant(storedEntry.variant) : '');
      this.gridContainer = root.querySelector('#ProductGridContainer');
      this.refreshQueued = false;
      this.fetching = false;
    }

    start() {
      if (!this.handle || !this.collectionUrl || !this.gridContainer) return;

      this.syncNavigation();
      this.refresh();
      this.gridObserver = new MutationObserver(() => this.queueRefresh());
      this.gridObserver.observe(this.gridContainer, { childList: true, subtree: true });
      document.addEventListener('click', this.handleLinkClick, true);
      window.addEventListener('popstate', this.handleHistoryChange);
    }

    normalizeHandle(value) {
      if (!value) return '';
      let handle = value.trim().toLowerCase();
      for (let pass = 0; pass < 2 && /%[0-9a-f]{2}/i.test(handle); pass += 1) {
        try {
          handle = decodeURIComponent(handle);
        } catch {
          return '';
        }
      }
      return /^[a-z0-9][a-z0-9-]{0,254}$/.test(handle) ? handle : '';
    }

    normalizeVariant(value) {
      return value && /^\d{6,20}$/.test(value) ? value : '';
    }

    collectionHandle(value) {
      try {
        const url = new URL(value, window.location.origin);
        const parts = url.pathname.split('/').filter(Boolean);
        return parts[0] === 'collections' ? this.normalizeHandle(parts[1]) : '';
      } catch {
        return '';
      }
    }

    handleLinkClick = (event) => {
      const link = event.target.closest('.pagination-wrapper a, facet-remove a, .active-facets a');
      if (link) this.decorateLink(link);
    };

    handleHistoryChange = () => this.queueRefresh();

    queueRefresh() {
      if (this.refreshQueued) return;
      this.refreshQueued = true;
      window.requestAnimationFrame(() => {
        this.refreshQueued = false;
        this.syncNavigation();
        this.refresh();
      });
    }

    syncNavigation() {
      this.root.querySelectorAll('form').forEach((form) => {
        this.ensureHiddenField(form, 'first', this.handle);
        if (this.variant) this.ensureHiddenField(form, 'variant', this.variant);
      });
      this.root
        .querySelectorAll('.pagination-wrapper a, facet-remove a, .active-facets a')
        .forEach((link) => this.decorateLink(link));
    }

    ensureHiddenField(form, name, value) {
      let field = form.querySelector(`input[type="hidden"][name="${name}"]`);
      if (!field) {
        field = document.createElement('input');
        field.type = 'hidden';
        field.name = name;
        form.append(field);
      }
      field.value = value;
    }

    decorateLink(link) {
      const href = link.getAttribute('href');
      if (!href) return;
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      url.searchParams.set('first', this.handle);
      if (this.variant) url.searchParams.set('variant', this.variant);
      link.href = url.href;
    }

    currentGrid() {
      return this.root.querySelector('#ProductGridContainer #product-grid');
    }

    findCards(grid) {
      return [...grid.children].filter((card) => card.dataset.pupsProductHandle === this.handle);
    }

    hasActiveFilters() {
      return [...new URLSearchParams(window.location.search).keys()].some((key) => key.startsWith('filter.'));
    }

    async refresh() {
      if (this.fetching) return;
      const grid = this.currentGrid();
      if (!grid) return;
      const existingCards = this.findCards(grid);

      if (existingCards.length > 0 && !this.variant) {
        this.pinCard(grid, existingCards[0]);
        return;
      }
      if (existingCards.length === 0 && this.hasActiveFilters()) return;

      this.fetching = true;
      try {
        const fetchedCard = await this.fetchCard();
        const currentGrid = this.currentGrid();
        if (!currentGrid) return;
        const fallbackCards = this.findCards(currentGrid);
        if (fetchedCard && (!this.hasActiveFilters() || fallbackCards.length > 0)) {
          this.pinCard(currentGrid, fetchedCard);
        } else if (fallbackCards.length > 0) {
          this.pinCard(currentGrid, fallbackCards[0]);
        }
      } finally {
        this.fetching = false;
      }
    }

    async fetchCard() {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 5000);
      const productUrl = new URL(
        `${this.collectionUrl.replace(/\/$/, '')}/products/${encodeURIComponent(this.handle)}`,
        window.location.origin,
      );
      productUrl.searchParams.set('view', 'pups-ad-card');
      if (this.variant) productUrl.searchParams.set('variant', this.variant);
      ['preview_theme_id', '_fd', 'pb'].forEach((key) => {
        if (this.params.has(key)) productUrl.searchParams.set(key, this.params.get(key));
      });

      try {
        const [response, variantMedia] = await Promise.all([
          fetch(productUrl.href, {
            credentials: 'same-origin',
            signal: controller.signal,
          }),
          this.variant ? this.fetchVariantMedia(controller.signal) : Promise.resolve(null),
        ]);
        if (!response.ok) return null;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const wrapper = html.querySelector('[data-pups-ad-card]');
        if (!wrapper || wrapper.dataset.pupsAdCard !== this.handle) return null;
        const card = wrapper.querySelector('li[data-pups-product-handle]');
        if (!card || card.dataset.pupsProductHandle !== this.handle) return null;

        const validatedVariant = wrapper.dataset.pupsAdVariant;
        const importedCard = document.importNode(card, true);
        if (this.variant && validatedVariant === this.variant) {
          this.rewriteProductLinks(importedCard, validatedVariant);
          this.rewriteCardMedia(importedCard, variantMedia);
        }
        return importedCard;
      } catch {
        return null;
      } finally {
        window.clearTimeout(timer);
      }
    }

    async fetchVariantMedia(signal) {
      const productJsonUrl = new URL(`/products/${encodeURIComponent(this.handle)}.js`, window.location.origin);
      const response = await fetch(productJsonUrl.href, { credentials: 'same-origin', signal });
      if (!response.ok) return null;
      const productData = await response.json();
      const variant = productData.variants?.find((candidate) => String(candidate.id) === this.variant);
      const media = variant?.featured_image;
      if (!media?.src) return null;
      return {
        src: media.src,
        alt: media.alt || '',
        width: Number(media.width) || 0,
        height: Number(media.height) || 0,
      };
    }

    rewriteCardMedia(card, media) {
      const image = card.querySelector('img');
      if (!image || !media?.src) return;
      const srcset = image.getAttribute('srcset') || '';
      const widths = [...srcset.matchAll(/\s(\d+)w(?:,|$)/g)].map((match) => match[1]);
      const imageUrl = new URL(media.src, window.location.origin);
      const urlForWidth = (width) => {
        const url = new URL(imageUrl.href);
        url.searchParams.set('width', width);
        return url.href;
      };
      if (widths.length > 0) {
        image.srcset = widths.map((width) => `${urlForWidth(width)} ${width}w`).join(', ');
        image.src = urlForWidth(widths[widths.length - 1]);
      } else {
        image.src = imageUrl.href;
      }
      if (media.alt) image.alt = media.alt;
      if (media.width) image.width = media.width;
      if (media.height) image.height = media.height;
    }

    rewriteProductLinks(card, variant) {
      card.querySelectorAll('a[href]').forEach((link) => {
        const url = new URL(link.href, window.location.origin);
        const pathParts = url.pathname.split('/').filter(Boolean);
        const productIndex = pathParts.lastIndexOf('products');
        if (productIndex === -1 || pathParts[productIndex + 1] !== this.handle) return;
        url.searchParams.set('variant', variant);
        link.href = url.href;
      });
      card.querySelectorAll('[data-product-url]').forEach((element) => {
        const url = new URL(element.dataset.productUrl, window.location.origin);
        url.searchParams.set('variant', variant);
        element.dataset.productUrl = `${url.pathname}${url.search}`;
      });
    }

    pinCard(grid, card) {
      this.findCards(grid).forEach((duplicate) => {
        if (duplicate !== card) duplicate.remove();
      });
      card.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
      card.style.removeProperty('--animation-order');
      card.dataset.pupsAdPinned = 'true';
      const image = card.querySelector('img');
      if (image) {
        image.loading = 'eager';
        image.fetchPriority = 'high';
      }
      if (grid.firstElementChild !== card) grid.prepend(card);
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('collection-component[data-pups-ad-pin]').forEach((root) => {
      if (root.dataset.pupsAdPinReady === 'true') return;
      root.dataset.pupsAdPinReady = 'true';
      new PupsCollectionAdPin(root).start();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
